import {
  Button,
  ButtonProps,
  Circle,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuList,
  Portal,
  Radio,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { CaretDown, CaretUp } from 'phosphor-react'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { SelectOption } from '@/ui/types/SelectOption'
import { textSort } from '@/ui/utils/sorts'

export const CustomSelect = forwardRef<
  HTMLButtonElement,
  {
    options: SelectOption[]
    sort?: boolean
    value?: string
    onChange?: (value: string) => void
    placeholder?: string
    disabled?: boolean
    name?: string
    onBlur?: () => void
    hideSelectedOption?: boolean
    triggerButton?: MenuButtonProps & ButtonProps
    isTagStyle?: boolean
    isTriggerWidthLimit?: boolean
    showRadio?: boolean
    maxHeight?: string
    withPortal?: boolean
    scrollToValue?: boolean
    isLoading?: boolean
    isInvalid?: boolean
  }
>(function CustomSelect(
  {
    options,
    value,
    onChange,
    sort = false,
    placeholder = 'Selecione...',
    disabled,
    name,
    onBlur,
    hideSelectedOption,
    triggerButton,
    isTagStyle,
    isTriggerWidthLimit,
    showRadio = false,
    maxHeight,
    withPortal,
    scrollToValue,
    isLoading,
    isInvalid
  },
  ref
) {
  const { t } = useTranslation()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedOptionRef = useRef<HTMLButtonElement>(null)
  const selectedOption = options.find((option) => option.value === value)

  const { isOpen, onOpen, onClose } = useDisclosure()

  useEffect(() => {
    if (scrollToValue && isOpen) selectedOptionRef.current?.scrollIntoView(true)
  }, [selectedOptionRef.current, isOpen, scrollToValue])

  const optionsToRender = sort
    ? options.sort(({ label: optionA }, { label: optionB }) =>
        textSort(optionA, optionB)
      )
    : options

  useImperativeHandle(ref, () => buttonRef.current!, [buttonRef.current])

  const ColorDot = ({ color }: { color?: string }) =>
    color ? <Circle size={'24px'} bg={color} display="inline-block" /> : null

  const MList = (
    <MenuList
      minW={buttonRef.current?.clientWidth}
      maxW={isTriggerWidthLimit ? buttonRef.current?.clientWidth : undefined}
      maxH={maxHeight ? maxHeight : undefined}
      overflowY={maxHeight ? 'auto' : undefined}
      border={'solid 1px #DEE2E6'}
      p={0}
      zIndex={2}
    >
      {(hideSelectedOption
        ? optionsToRender.filter(
            ({ value: optValue, isUnselectable }) =>
              optValue !== value && !isUnselectable
          )
        : optionsToRender
      ).map((option) => (
        <MenuItem
          key={option.value}
          ref={
            !hideSelectedOption && option.value === value
              ? selectedOptionRef
              : undefined
          }
          onClick={() => onChange?.(option.value)}
          borderBottom={'solid 1px #DEE2E6'}
          _hover={{ bg: 'custom.gray.900' }}
          px={2}
          py={3}
          overflow="hidden"
          isDisabled={option?.isUnselectable}
        >
          <Flex gap={1} align={'center'}>
            {showRadio && (
              <Radio
                isChecked={option.value === value}
                pointerEvents="none"
                size="lg"
                colorScheme="blue"
              />
            )}
            <ColorDot color={option.tagColor} />
            {option?.leftIcon}
            <Text
              bgColor={option.backgroundColor}
              color={option.color || 'custom.gray.100'}
              borderRadius={option.borderRadius}
              px={2}
              py={1}
            >
              {t(option.label, { defaultValue: option.label })}
            </Text>
          </Flex>
        </MenuItem>
      ))}
    </MenuList>
  )

  const caretIcon = disabled ? (
    <></>
  ) : isOpen ? (
    <Icon as={CaretUp} m={0} />
  ) : (
    <Icon as={CaretDown} m={0} />
  )

  return (
    <Menu isLazy isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <>
        <MenuButton
          as={Button}
          width="100%"
          fontWeight={400}
          bg="white"
          border={'solid'}
          borderWidth={isInvalid ? '2px' : '1px'}
          borderColor={
            isInvalid
              ? 'rgb(229, 62, 62)'
              : selectedOption
                ? 'custom.gray.400'
                : 'custom.gray.600'
          }
          css={`
            &[disabled]:active {
              background-color: unset;
            }
            &[aria-expanded='true'] {
              background-color: #f5f5ff;
            }
          `}
          _hover={{ textDecoration: 'none' }}
          ref={buttonRef}
          textAlign="initial"
          isDisabled={disabled || isLoading}
          _disabled={{
            color: 'custom.gray.400',
            bgColor: 'custom.gray.700',
            opacity: 1,
            border: 'none'
          }}
          name={name}
          onBlur={onBlur}
          rightIcon={isTagStyle ? undefined : caretIcon}
          h={'2.5rem'}
          color={
            (selectedOption?.color ?? selectedOption)
              ? 'custom.gray.100'
              : 'custom.gray.300'
          }
          bgColor={selectedOption?.backgroundColor}
          borderRadius={selectedOption?.borderRadius}
          {...triggerButton}
        >
          {isLoading ? (
            <Flex
              gap={2}
              align={'center'}
              px={isTagStyle ? 2 : 0}
              py={isTagStyle ? 1 : 3}
            >
              <Spinner />
            </Flex>
          ) : selectedOption ? (
            <Flex gap={2} align={'center'}>
              <ColorDot color={selectedOption.tagColor} />
              {selectedOption?.leftIcon}
              <Text
                as={'span'}
                textAlign={'start'}
                display={'inline-flex'}
                gap={'1'}
                alignItems={'center'}
                px={isTagStyle ? 2 : 0}
                py={isTagStyle ? 1 : 3}
                isTruncated
              >
                {t(selectedOption.label, {
                  defaultValue: selectedOption.label
                })}
                {isTagStyle && caretIcon}
              </Text>
            </Flex>
          ) : (
            <Text
              as={'span'}
              textAlign={'start'}
              display={'inline-flex'}
              gap={'1'}
              alignItems={'center'}
              px={0}
              py={3}
            >
              {placeholder}
              {isTagStyle && caretIcon}
            </Text>
          )}
        </MenuButton>

        {withPortal ? <Portal>{MList}</Portal> : MList}
      </>
    </Menu>
  )
})
